/**
 * Global Search — Phase 5.
 * Cmd+K / Ctrl+K opens a modal that searches across students, staff, requisitions,
 * submissions, and users (super_admin only). Debounced backend call.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Users, GraduationCap, ClipboardList, FileText, Shield, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_META = {
  students:     { label: 'Students',     icon: GraduationCap, color: 'text-blue-600' },
  staff:        { label: 'Staff',        icon: Users,         color: 'text-green-600' },
  requisitions: { label: 'Requisitions', icon: ClipboardList, color: 'text-orange-600' },
  submissions:  { label: 'Submissions',  icon: FileText,      color: 'text-purple-600' },
  users:        { label: 'Users',        icon: Shield,        color: 'text-red-600' },
};

const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ('');
      setResults(null);
    }
  }, [open]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/search/global`, { params: { q } });
        setResults(res.data);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (url) => {
    onClose();
    navigate(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden [&>button.absolute]:hidden">
        <div className="border-b">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search students, staff, requisitions..."
              className="pl-11 pr-12 h-14 border-0 text-base focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="global-search-input"
            />
            {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded border" style={{ display: loading ? 'none' : 'block' }}>ESC</span>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!results && q.length < 2 && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Type at least 2 characters to search across the platform.
            </div>
          )}
          {results && results.total === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No matches for <strong>"{results.query}"</strong>.
            </div>
          )}
          {results && results.total > 0 && (
            <div className="p-2">
              {Object.entries(results.groups).map(([key, items]) => {
                if (!items?.length) return null;
                const meta = GROUP_META[key];
                const Icon = meta.icon;
                return (
                  <div key={key} className="mb-3">
                    <div className="px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1.5">
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                      {meta.label}
                      <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1">{items.length}</Badge>
                    </div>
                    {items.map((it) => (
                      <button
                        key={`${key}-${it.id}`}
                        onClick={() => go(it.url)}
                        className="w-full text-left px-3 py-2.5 rounded-md hover:bg-muted/50 flex items-center gap-3 transition-colors"
                        data-testid={`search-result-${key}-${it.id}`}
                      >
                        <Icon className={`h-4 w-4 ${meta.color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{it.title}</div>
                          <div className="text-xs text-muted-foreground truncate">{it.subtitle}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t px-4 py-2 text-[11px] text-muted-foreground bg-muted/30 flex justify-between">
          <span>Tip: press <kbd className="bg-muted px-1.5 rounded border">⌘ K</kbd> anywhere to open search</span>
          {results && <span>{results.total} result{results.total !== 1 && 's'}</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
